import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { Policy, PolicyPrice } from '../../policies/models/Policy';
import { Reservation, ReservationGuest } from '../models/Reservation';
import { OrganizationService } from '../../organizations/services/organizations.service';
import { ReservationService } from '../services/reservation.service';
import { NavService } from '../../../layout/nav/nav.service';
import { Country } from '../../../shared/address/Country';
import { ErrorModalComponent } from '../../../shared/components/error-modal/error-modal.component';
import { CountryService } from '../../../shared/reference/country.service';
import { SearchService } from '../../../shared/search/search.service';
import { NotificationService } from '../../../utilities/PortalNotificationUtility';
import { isStringEmptyOrNull } from '../../../utilities/StringUtility';
import { Currency } from '../../currencies/models/Currency';
import { CurrenciesService } from '../../currencies/services/currencies.service';
import { Property } from '../../properties/models/Property';
import { VerificationPackage } from '../../verification-packages/models/VerificationPackage';
import { VerificationPackagesService } from '../../verification-packages/services/verification-packages.service';
import { Logger } from '../../../infrastructure/logging/logger';
import { BasicInformationStepComponent } from './components/basic-information-step/basic-information-step.component';
import { GuestsStepComponent } from './components/guests-step/guests-step.component';
import { PropertyStepComponent } from './components/property-step/property-step.component';
import { ReviewAllComponent } from './components/review-all/review-all.component';
import { PropertyApiService } from '../../properties/services/property.api.service';
import { Organization } from '../../organizations/models/Organization';

@Component({
  selector: 'app-reservation-form',
  templateUrl: './reservation-form.component.html',
  styleUrls: ['./reservation-form.component.scss'],
  providers: [ CurrenciesService, CurrencyPipe ],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatIcon,
    MatExpansionModule,
    MatButtonModule,
    RouterModule,
    BasicInformationStepComponent,
    GuestsStepComponent,
    PropertyStepComponent,
    ReviewAllComponent
  ]
})
export class ReservationFormComponent implements OnInit {

  basicInformationForm!: FormGroup;
  guestsForm!: FormGroup;

  loading = new BehaviorSubject<boolean>(false);
  submitting: boolean = false;

  organizationId!: number | string;
  selectedOrganization: any;
  reservation!: Reservation;
  verificationPackage!: VerificationPackage;
  organizations!: Organization[];
  currencies!: Currency[];
  countries!: Country[];
  properties!: Property[];
  selectedProperty!: any;
  existingProperty?: any;
  addedGuests: ReservationGuest[] = [];
  existingReservationGuests!: ReservationGuest[];
  existingReservationGuestCount: number = 0;

  reservationId!: number;
  existingReservation: boolean = false;
  keyword!: string;

  policyPriceDetail?: PolicyPrice;
  primaryReservationPolicy!: Policy;
  primaryPolicyPricingModel!: string;
  primaryOrgPolicy!: Policy;
  selectedPrimaryPolicy!: Policy;
  orgPolicyTableData!: any[];
  insurancePolicyPrice: number = 0;
  numberOfNightsFrom: number = 0;
  numberOfNightsTo: number = 0;
  extraNights: number = 0;
  extraNightsPrice: number = 0;
  reservationLength: number = 0;
  numberOfGuests: number = 0;
  insuranceCost: number = 0;
  verificationCost: number = 0;
  invalidForm: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private currenciesService: CurrenciesService,
    private organizationService: OrganizationService,
    private verificationPackagesService: VerificationPackagesService,
    private propertiesService: PropertyApiService,
    private reservationService: ReservationService,
    private router: Router,
    private snackbar: MatSnackBar,
    public errorMessageMatDialog: MatDialog,
    private route: ActivatedRoute,
    private countryService: CountryService,
    private searchService: SearchService,
    private notificationService: NotificationService,
    private logService: Logger,
    private navService: NavService
  ) { }

  ngOnInit() {
    this.loading.next(true);
    this.resetSearchQuery();

    if (this.navService.selectedOrgId.value > 0) {
      this.organizationId = this.navService.selectedOrgId.value;
      this.searchService.setOrganizationId(Number(this.organizationId));
      this.loadOrganizationVerificationPackages();
      this.loadPrimaryOrganizationPolicy();
    }
    
    this.initForms();

    this.reservationId = Number(this.getReservationIdFromUrl());
    this.loadPage();
  }

  resetSearchQuery() {
    this.searchService.resetQuery();
    this.searchService.setSort('propertyId', -1);
  }

  async loadPage() {
    await this.loadReservationFormData();
  }

  async loadOrganizationVerificationPackages() {
    const verificationPackages = await this.verificationPackagesService.getVerificationPackagesByOrgId(Number(this.organizationId));
    if (verificationPackages.length === 0) {
      this.verificationCost = 0;
    } else {
      this.invalidForm = false;
      this.verificationPackage = verificationPackages.find(verifPackage => Number(verifPackage.is_primary) === 1)!;
    }
  }

  async loadReservationFormData() {
    try {
      this.countries = await this.countryService.getCountries();

      const currenciesResponse = await this.currenciesService.getCurrencies();
      this.currencies = currenciesResponse;

      const orgResponse = await this.organizationService.getOrganizations();
      let orgsWtihVerification = orgResponse.filter(org => org.for_reservation === 1);
      this.organizations = orgsWtihVerification.sort((a,b) => (a.organization_name > b.organization_name) ? 1 : ((b.organization_name > a.organization_name) ? -1 : 0));

      if (this.reservationId) {
        const reservation = await this.reservationService.getReservationById(this.reservationId);
        this.reservation = reservation;
        this.organizationId = reservation.organization_id.toString();
        this.existingReservation = true;
        await this.loadFromReservation(reservation);
        await this.loadPrimaryOrganizationPolicy();
        await this.loadOrganizationVerificationPackages();
        this.existingReservationGuests = reservation.guests;
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while loading Reservation form', 'Error while retrieving Reservation form data');
    } finally {
      this.loading.next(false);
    }
  }

  async loadFromReservation(reservation: Reservation) {
    let organization = this.organizations.find(org => org.organization_id === reservation.organization_id);

    this.basicInformationForm.patchValue(reservation);
    this.basicInformationForm.get('organization')?.patchValue(organization);
    this.basicInformationForm.markAllAsTouched();
    this.basicInformationForm.markAsDirty();

    this.existingProperty = reservation.property;
    this.selectedProperty = this.existingProperty;

    this.insuranceCost = reservation.insurance_cost;
    this.verificationCost = reservation.verification_cost;
    this.existingReservationGuestCount = reservation.number_of_guests;
  }

  async loadProperties() {
    try {
      const { properties } = await this.propertiesService.searchProperties(this.searchService.currentQuery);
      this.properties = properties;
      if (this.properties.length === 0) {
        this.invalidForm = true;
        this.logService.handleError(`No Properties found for orgId: ${this.organizationId}`, 'No Properties found for Organization, please assign one to submit Reservation', `No properties found for orgId: ${this.organizationId}`);
        return;
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while loading properties for this reservation', 'Error while retrieving Properties');
    }
  }

  getReservationIdFromUrl() {
    return this.route.snapshot.queryParamMap.get('reservationId');
  }

  async getSelectedOrgPolicies(orgId: number) {
    this.organizationId = orgId;
    this.selectedOrganization = this.organizations.find(org => org.organization_id === orgId);

    this.searchService.setOrganizationId(this.organizationId);
    const { properties } = await this.propertiesService.searchProperties(this.searchService.currentQuery);
    this.properties = properties;
    if (this.properties.length === 0) {
      this.invalidForm = true;
      this.logService.handleError(`No Properties found for orgId: ${this.organizationId}`, 'No Properties found for Organization, please assign one to submit Reservation', `No properties found for orgId: ${this.organizationId}`);
      return;
    }
    if (this.selectedOrganization.for_reservation) {
      await this.loadPrimaryOrganizationPolicy();
    }
    if (this.selectedOrganization.for_guest_verification) {
      await this.loadOrganizationVerificationPackages();
    }
  }

  async loadPrimaryOrganizationPolicy() {
    try {
      const policyResponse = await this.organizationService.getPoliciesByOrganizationId(Number(this.organizationId));      
      if (!policyResponse || policyResponse.length == 0) {
        this.invalidForm = true;
        this.logService.handleError(`No organization policies found for orgId: ${this.organizationId}`, 'No Policies found for Organization, please assign one to submit Reservation', `No organization policies found for orgId: ${this.organizationId}`);
        return;
      } else {
        this.invalidForm = false;
      }

      const primaryOrgPolicy = policyResponse.find(policy => Number(policy.is_primary) == 1);
      if (!primaryOrgPolicy) {
        this.logService.handleError(null, 'No primary Organization policy found', `Primary organization policy not found for orgId: ${this.organizationId}`);
        return;
      }

      this.primaryOrgPolicy = primaryOrgPolicy;
      this.primaryReservationPolicy = primaryOrgPolicy;
      this.selectedPrimaryPolicy = primaryOrgPolicy;

      await this.updateInsuranceCostDetails(this.primaryOrgPolicy);
      this.orgPolicyTableData = policyResponse.filter(policy => Number(policy.is_primary) === 1);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving the primary Organization policy', 'Error while retrieving Primary Organization Policy');
    }
  }

  async updateInsuranceCostDetails(policy: Policy) {
    const policyPriceDetail = await this.propertiesService.getOrganizationPolicyPriceDetail(Number(policy.policy_price.id), this.organizationId);
    if (policyPriceDetail == null) {
      this.logService.handleError(null, 'An error occurred while retrieving Policy price details', 'Error while retrieving policy price details');
      return;
    }
    this.policyPriceDetail = policyPriceDetail;

    switch (this.policyPriceDetail.pricing_model_id) {
      case 1:
        this.handleInsuranceCostRange(policyPriceDetail, this.reservationLength > 0);
        break;
      case 2:
      case 3:
        this.primaryPolicyPricingModel = policyPriceDetail.pricing_model.display_text;
        this.insurancePolicyPrice = policyPriceDetail.price;
        this.insuranceCost = policyPriceDetail.price;
        break;
      default:
        this.logService.handleError(null, 'An error occurred while setting Insurance cost', `Invalid policy pricing model ID: ${policyPriceDetail.pricing_model_id}`);
        break;
    }
  }

  onSearchEvent(keyword: string) {
    this.keyword = keyword;
    this.searchService.addTextQuery(keyword);
    this.searchService.setPage(1);
    this.loadProperties();
  }

  private handleInsuranceCostRange(policyPrice: PolicyPrice, multipleDayReservation: boolean = false) {
    if (multipleDayReservation) {
      this.updatePolicyAfterReservationChange(this.reservationLength);
      return;
    }

    const nightRange = policyPrice.night_ranges[0];
    if (nightRange == null) {
      this.logService.handleError(null, 'An error occurred while setting the Insurance cost range', 'Error while setting insurance cost range, night range is null');
      return;
    }

    const {
      number_of_nights_from,
      number_of_nights_to,
      price
    } = nightRange;

    this.insuranceCost = price;
    this.insurancePolicyPrice = price;
    this.numberOfNightsFrom = number_of_nights_from;
    this.numberOfNightsTo = number_of_nights_to;

    this.primaryPolicyPricingModel = policyPrice.pricing_model.display_text;
  }

  updatePolicyAfterReservationChange(updatedReservationLength: number) {
    this.reservationLength = updatedReservationLength;
    if (!this.policyPriceDetail) {
      this.logService.handleError(null, 'An error occurred while updating the Policy after reservation change', 'Error while updating policy after reservation change, policy price detail is null');
      return;
    }

    const pricingModelId = this.policyPriceDetail.pricing_model_id;
    if (pricingModelId == 1) {
      this.primaryPolicyPricingModel = this.policyPriceDetail.pricing_model.display_text;
      this.resetExtraNightsAndPrice();
      this.calculateNightlyPolicyPrice(this.policyPriceDetail);
    } else if (pricingModelId == 2) {
      this.insuranceCost = this.policyPriceDetail.price * this.reservationLength;
      this.insurancePolicyPrice = this.policyPriceDetail.price;
      this.numberOfNightsTo = this.reservationLength;
    } else if (pricingModelId == 3) {
      this.insuranceCost = this.policyPriceDetail.price;
      this.insurancePolicyPrice = this.policyPriceDetail.price;
      this.numberOfNightsTo = this.reservationLength;
    }
  }

  calculateNightlyPolicyPrice(policyPriceDetail: PolicyPrice) {
    const nightRange = policyPriceDetail.night_ranges.find(nr => nr.number_of_nights_to >= this.reservationLength);
    if (nightRange) {
      this.insuranceCost = nightRange.price;
      this.insurancePolicyPrice = nightRange.price;
      this.numberOfNightsFrom = nightRange.number_of_nights_from;
      this.numberOfNightsTo = nightRange.number_of_nights_to;
    } else {
      let maxPolicyNightPriceRange = policyPriceDetail.night_ranges[policyPriceDetail.night_ranges.length - 1];
      this.insuranceCost = maxPolicyNightPriceRange.price;
      this.numberOfNightsFrom = maxPolicyNightPriceRange.number_of_nights_from;
      this.numberOfNightsTo = maxPolicyNightPriceRange.number_of_nights_to;

      this.extraNights = this.reservationLength - maxPolicyNightPriceRange.number_of_nights_to;
      this.extraNightsPrice = this.extraNights * policyPriceDetail.extra_night_price;
      this.insuranceCost = maxPolicyNightPriceRange.price + this.extraNightsPrice;
      this.insurancePolicyPrice = maxPolicyNightPriceRange.price;
    }
  }

  async getPrimaryPropertyPolicy(property: Property) {
    try {
      const allAvailablePolicies = await this.propertiesService.getPropertyPolicies(property.property_id);
      const availablePolicies = allAvailablePolicies.filter(policy => policy.status_id === 1);
      if (!availablePolicies || availablePolicies.length == 0) {
        this.primaryReservationPolicy = this.primaryOrgPolicy;
        this.selectedPrimaryPolicy = this.primaryOrgPolicy;
        await this.updateInsuranceCostDetails(this.primaryOrgPolicy);
        this.displayOrganizationPolicySuccessSnackbar();
        return;
      }

      const primaryPolicy = availablePolicies.find(policy => policy.is_primary);
      if (primaryPolicy == null) {
        this.logService.handleError(null,
          'No primary policy found for the selected property',
          `Primary policy not found for property: ${property.property_id.toString()}`);
        return;
      }
      this.primaryReservationPolicy = primaryPolicy;
      this.selectedPrimaryPolicy = this.primaryReservationPolicy;

      const policyPriceDetail = primaryPolicy.policy_price;
      if (policyPriceDetail == null) {
        this.logService.handleError(null,
          'No policy price detail found for the selected property',
          `No policy price detail found for property: ${property.property_id.toString()}`);
        return;
      }
      this.policyPriceDetail = policyPriceDetail;
      this.setPolicyPriceDetails(policyPriceDetail);
      this.displayPropertyPolicySuccessSnackbar();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving the Primary Property Policy', 'Error while retrieving Primary Property Policy');
    }
  }

  private setPolicyPriceDetails(policyPriceDetail: PolicyPrice) {
    switch (policyPriceDetail.pricing_model_id) {
      case 1:
        this.primaryPolicyPricingModel = policyPriceDetail.pricing_model.display_text;
        this.resetExtraNightsAndPrice();

        if (this.insuranceCost === 0) {
          this.insuranceCost = policyPriceDetail.night_ranges[0].price;
          this.insurancePolicyPrice = policyPriceDetail.night_ranges[0].price;
          this.numberOfNightsFrom = policyPriceDetail.night_ranges[0].number_of_nights_from;
          this.numberOfNightsTo = policyPriceDetail.night_ranges[0].number_of_nights_to;
        } else {
          this.calculateNightlyPolicyPrice(policyPriceDetail);
        }
        break;
      case 2:
        this.primaryPolicyPricingModel = policyPriceDetail.pricing_model.display_text;
        this.insurancePolicyPrice = policyPriceDetail.price;

        if (this.reservationLength === 0) {
          this.insuranceCost = policyPriceDetail.price;
        } else {
          this.insuranceCost = policyPriceDetail.price * this.reservationLength;
          this.numberOfNightsTo = this.reservationLength;
        }
        break;
      case 3:
        this.primaryPolicyPricingModel = policyPriceDetail.pricing_model.display_text;
        this.insurancePolicyPrice = policyPriceDetail.price;
        this.insuranceCost = policyPriceDetail.price;
        this.numberOfNightsTo = this.reservationLength;
        break;
      default:
        this.logService.handleError(`Invalid policy pricing model ID: ${policyPriceDetail.pricing_model_id}`,
          'An error occurred while populating the Policy price detail',
          `Invalid policy pricing model ID: ${policyPriceDetail.pricing_model_id}`);
        break;
    }
  }

  resetExtraNightsAndPrice() {
    this.extraNights = 0;
    this.extraNightsPrice = 0;
  }

  initForms() {
    this.basicInformationForm = this.formBuilder.group({
      organization: ['', Validators.required],
      reservation_reference_id: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      arrival_date: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      departure_date: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      booking_date: ['', Validators.pattern(/[\S]/)],
      reservation_cost: ['', Validators.pattern(/[\S]/)],
      reservation_currency_id: ['']
    });

    this.guestsForm = this.formBuilder.group({
      first_name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      middle_name: ['', Validators.pattern(/[\S]/)],
      last_name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      birth_date: [null, Validators.pattern(/[\S]/)],
      email: ['', Validators.email],
      phone_number: ['', Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)],
      address: this.formBuilder.group({
        address_line_1: ['', Validators.pattern(/[\S]/)],
        address_line_2: ['', Validators.pattern(/[\S]/)],
        city: ['', Validators.pattern(/[\S]/)],
        state: [''],
        postal_code: ['', Validators.pattern(/^\d+$/)],
        country_id: [0],
        country_name: ['']
      }),
      verification_package: [''],
      verification_cost: ['']
    });
  }

  canCreateReservation(): boolean {
    let property_id = 0;
    if (this.selectedProperty && this.selectedProperty.property_id > 0) {
      property_id = this.selectedProperty.property_id;
    }

    if (!this.invalidForm) {
      if (!this.existingReservation) {
        return (
          this.basicInformationForm?.valid &&
          this.addedGuests?.length > 0 &&
          property_id > 0
        );
      } else {
        return (
          this.basicInformationForm.valid &&
          property_id > 0
        );
      }
    } else {
      return false;
    }
  }

  guestsAdded(reservationGuests: any[]) {
    this.addedGuests = reservationGuests;
    if(this.verificationPackage && this.verificationPackage?.price > 0) {
      this.verificationCost = this.verificationPackage.price * this.addedGuests.length;
    } else {
      this.verificationCost = 0;
    }
  }

  async onPropertySelected(propertyId: number) {
    try {
      if (!propertyId) {
        return;
      }

      this.selectedProperty = await this.propertiesService.getPropertyDetail(propertyId);
      this.getPrimaryPropertyPolicy(this.selectedProperty);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while selecting Property', 'Error while selecting Property');
    }
  }

  async submitReservation() {
    try {
      this.submitting = true;
      let reservation = await this.buildReservation();

      if (this.existingReservation) {
        reservation.guests = this.reservation.guests;
        const response = await this.reservationService.updateReservation(this.reservationId, reservation);
        if (response?.reservation_id) {
          this.navigateAfterSubmit(response.reservation_id);
        }
      } else {
        let response = await this.reservationService.createReservation(reservation);
        this.reservationId = response.reservation_id;
        this.existingReservation = true;
        this.navigateAfterSubmit(this.reservationId);
      }
    } catch (e: any) {
      if (!this.existingReservation) {
        this.restoreFormStateAfterError();
      }
      this.logService.handleError(e, 'An error occurred while submitting Reservation', 'Error while submitting Reservation');

      if (e instanceof HttpErrorResponse && 
        (e?.error?.err?.code == "1300" || e?.error?.code == "1300")) {
        this.logService.handleError(e, 'Reservation refernce ID already exists', 'Reservation refernce ID already exists');
      }
    } finally {
      this.submitting = false;
    }
  }

  //TODO: We shouldn't need this function, this form's state management needs to be reworked
  private restoreFormStateAfterError() {
    this.existingProperty = this.selectedProperty;
    this.existingReservationGuestCount = this.addedGuests.length;
    this.existingReservationGuests = this.addedGuests;
  }

  navigateAfterSubmit(reservationId: any) {
    if (!reservationId) {
      this.notificationService.showNotification({ message: 'An error occurred while submitting the reservation'});
    } else {
      this.navigateToReservationDetailPage(this.reservationId);
    }
  }

  async buildReservation() {
    let reservation: any = {
      ...this.basicInformationForm.value,
      reservation_guests: this.addedGuests,
      property_id: this.selectedProperty.property_id,
      property: this.selectedProperty
    }

    if (this.selectedPrimaryPolicy) {
      reservation.selected_policies = [this.selectedPrimaryPolicy.insurance_policy_id];
    } else {
      reservation.selected_policies = [];
    }

    if (this.basicInformationForm.get('arrival_date')?.value && this.basicInformationForm.get('departure_date')?.value) {
      const timeDiff = Math.abs(new Date(this.basicInformationForm.get('departure_date')?.value).getTime() - new Date(this.basicInformationForm.get('arrival_date')?.value).getTime());
      reservation.number_of_nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
    }

    if (this.reservationId) {
      reservation.reservation_id = this.reservationId;
    }

    reservation.reservation_cost = this.basicInformationForm.get('reservation_cost')?.value ? this.basicInformationForm.get('reservation_cost')?.value : 0;

    reservation.insurance_cost = this.insuranceCost;
    reservation.updated_insurance_cost = this.insuranceCost;
    reservation.verification_cost = this.verificationCost;
    reservation.updated_verification_cost = this.verificationCost;
    reservation.for_guest_verification = 1;

    reservation.organization_id = this.organizationId;
    reservation.organization_name = this.selectedOrganization?.organization_name;
    delete reservation.organization;

    let referenceId = this.basicInformationForm.get('reservation_reference_id')?.value;
    if (!isStringEmptyOrNull(referenceId)) {
      reservation.reservation_reference_id = referenceId;
    } else {
      reservation.reservation_reference_id = null;
    }
    reservation.reservation_currency_id = 1;
    reservation.reservation_currency = this.currencies.find(currency => currency.id === 1);

    reservation.status = "UPCOMING";
    reservation.status_id = 1;
    reservation.status_history = [];

    let reservationGuestsArray: any[] = [];
    this.addedGuests.forEach(guest => {
      guest.selected_verification_package = guest.verification_package?.verification_package_id;
      reservationGuestsArray.push(guest);
    });
    reservation.reservation_guests = reservationGuestsArray;
    reservation.number_of_guests = this.numberOfGuests;

    let guestsArray: ReservationGuest[] = [];
    this.addedGuests.forEach(guest => {
      guest.selected_verification_package = guest.verification_package?.verification_package_id;
      guestsArray.push(guest);
    });
    reservation.guests = guestsArray;

    return reservation;
  }

  navigateToReservationDetailPage(reservationId: number) {
    this.router.navigate([`/reservations/detail/${reservationId}`]);
  }

  displayPropertyPolicySuccessSnackbar() {
    this.snackbar.open('Insurance Updated With Primary Property Policy', 'OK', { duration: 4000 });
  }

  displayOrganizationPolicySuccessSnackbar() {
    this.snackbar.open('Insurance Updated With Primary Organization Policy', 'OK', { duration: 4000 });
  }

  openErrorResponseDialog(errorResponse: any) {
    this.errorMessageMatDialog.open(ErrorModalComponent, {
      data: errorResponse,
      disableClose: true,
      width: '500px',
      height: '195px'
    });
  }

  handleNumberOfGuestsChanged(guests: String) {
    this.numberOfGuests = Number(guests);
  }

  loadingComplete() {
    let countriesLoaded: boolean = this.countries != null && this.countries.length > 0;
    return !this.loading && this.reservation && countriesLoaded;
  }

  onCancelClick() {
    this.router.navigate(['/reservations'])
  }
}